import {
  OLSON_TIMEZONE_API_URL,
  REGISTER_PAGE_URL,
} from '../../../constants/constants';
import { AttributionData } from '../classes/AttributionData';
import { SignupPayload } from '../classes/SignupPayload';
import { heap as $EC } from '../../../../tracking/evercommerce';

/**
 * Fetches timezone ID by Olson timezone name
 */
export async function fetchTimezoneIdByOlsonName(olsonName: string) {
  const encodedOlsonName = encodeURIComponent(olsonName);
  const olsonNameUrl = `${OLSON_TIMEZONE_API_URL}?olsonName=${encodedOlsonName}`;

  try {
    const response = await fetch(olsonNameUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.Id;
  } catch (error) {
    console.error('Error fetching timezone ID by Olson name:', error);
    throw error;
  }
}

/**
 * Fetches signup configuration data
 */
export async function fetchSignupConfig() {
  try {
    const response = await fetch('/umbraco/api/signup/configs', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.responseMessage || 'Failed to fetch configuration');
    }

    return data;
  } catch (error) {
    console.error('Error fetching signup config:', error);
    throw new Error(
      'Failed to load form configuration. Please refresh the page and try again.',
    );
  }
}

/**
 * Submits signup data to the API
 */
export async function submitSignupData(payload: SignupPayload) {
  try {
    const response = await fetch('/umbraco/api/signup/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          errorData.responseMessage ||
          `HTTP error! status: ${response.status}`,
      );
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(
        data.message ||
          data.responseMessage ||
          'Signup failed. Please try again.',
      );
    }

    return data;
  } catch (error) {
    console.error('Error submitting signup data:', error);
    throw error;
  }
}

/**
 * Maps form state to API payload according to specification
 */
export function createSignupPayload(
  state: any,
  formOptions: any,
): SignupPayload {
  const businessCategoryId = findIdByName(
    formOptions.categories,
    state.category,
  );
  const businessSizeId = findIdByName(formOptions.sizes, state.teamSize);
  const previousSystemId = determineBookingMethod(
    state.bookingMethod,
    state.otherSoftware,
    formOptions.previousSystems,
  );

  const attributionData = new AttributionData($EC.identify(state.email));

  return new SignupPayload(
    state.businessName,
    businessCategoryId,
    businessSizeId,
    state.phoneNumber,
    state.countryCode,
    state.promoCode || '',
    state.currency,
    state.email,
    state.firstName,
    state.lastName,
    state.password,
    previousSystemId,
    state.timeZone,
    state.gRecaptchaResponse,
    attributionData,
    state.marketingConsentGranted,
    state.consentTemplateId,
  );
}

function findIdByName(options: any, displayName: string): number | null {
  const entries = Array.isArray(options) ? options : Object.entries(options);
  const entry = entries.find(([_, value]) => value === displayName);

  return entry ? parseInt(entry[0]) : null;
}

function determineBookingMethod(
  bookingMethod: BookingMethod,
  otherSoftware: any,
  previousSystems: any,
): number | null {
  // Map booking method to previous system ID
  if (bookingMethod === 'Other software' && otherSoftware) {
    return findIdByName(previousSystems, otherSoftware);
  }

  // Map the booking method options to their corresponding IDs
  const bookingMethodMap: Record<BookingMethod, number> = {
    'Pen and paper': 1,
    'Other software': 7,
    'Walk-ins': 6,
    "I'm a new business": 8,
  };

  return bookingMethodMap[bookingMethod as BookingMethod] ?? null;
}

type BookingMethod =
  'Pen and paper' | 'Other software' | 'Walk-ins' | "I'm a new business";

interface State {
  bookingMethod: BookingMethod;
  otherSoftware?: string;
}
